import { Editor } from '@tiptap/react';
import { isHeading, isParagraph, isParagraphEmpty, selectionContainsHeading } from './editor-state';

/**
 * Command types for categorizing different commands
 */
export enum CommandType {
  AI = 'ai',
  FORMAT = 'format',
  STRUCTURE = 'structure',
  MEDIA = 'media'
}

/**
 * Command metadata registry for all commands in the editor
 */
export const COMMAND_METADATA: Record<string, {
  type: CommandType;
  description?: string;
  shortcut?: string;
}> = {
  // AI Commands
  "AI Prompt": {
    type: CommandType.AI,
    description: "Invoke Content Copilot with a custom prompt",
    shortcut: "Ctrl+."
  },
  "Generate Content": {
    type: CommandType.AI,
    description: "Generate content using Content Copilot",
    shortcut: "Ctrl+Shift+G"
  },
  "Refine Content": {
    type: CommandType.AI,
    description: "Refine selected content using Content Copilot",
    shortcut: "Ctrl+Shift+R"
  },
  "Summarize Title": {
    type: CommandType.AI,
    description: "Summarize title using Content Copilot",
    shortcut: "Ctrl+Shift+T"
  },
  "Create Outline": {
    type: CommandType.AI,
    description: "Create an outline from document"
  },
  "Slash Commands": {
    type: CommandType.STRUCTURE,
    description: "Open slash command menu",
    shortcut: "Ctrl+/"
  }
};

/**
 * Helper function to check if a command is an AI command
 */
export const isAICommand = (title: string): boolean => {
  return COMMAND_METADATA[title]?.type === CommandType.AI;
};

/**
 * Get all AI command titles
 */
export const getAICommandTitles = (): string[] => {
  return Object.keys(COMMAND_METADATA).filter(title =>
    COMMAND_METADATA[title].type === CommandType.AI
  );
};

/**
 * Get keyboard shortcut for a command
 */
export const getCommandShortcut = (title: string): string | undefined => {
  return COMMAND_METADATA[title]?.shortcut;
};

/**
 * Get all commands with their shortcuts
 */
export const getCommandsWithShortcuts = (): Array<{ title: string; shortcut: string; description?: string }> => {
  return Object.entries(COMMAND_METADATA)
    .filter(([, metadata]) => metadata.shortcut)
    .map(([title, metadata]) => ({
      title,
      shortcut: metadata.shortcut!,
      description: metadata.description
    }));
};

/**
 * Check if an AI command should be enabled based on editor context
 */
export const isAICommandEnabled = (commandTitle: string, editor: Editor | null): boolean => {
  if (!editor) return false;

  switch (commandTitle) {
    case "AI Prompt":
      return true; // Always enabled in agent mode - flexible for any AI task
    case "Generate Content":
      return !isHeading(editor);
    case "Refine Content":
      return !(
        (isParagraph(editor) && isParagraphEmpty(editor)) ||
        (!isParagraph(editor) && !editor.state.selection.content().size) ||
        selectionContainsHeading(editor)
      );
    case "Summarize Title":
      return isHeading(editor);
    case "Create Outline":
      return !!editor.getText().trim();
    default:
      return true; // Non-AI commands are always enabled
  }
};
